import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  HeartPulse,
  Bot,
  Shield,
  Trophy,
  UserPlus,
  ClipboardList,
  Watch,
  Users,
  Calendar,
  Settings,
  Sparkles,
} from '@/utils/icons';

/**
 * Guided on-screen tutorial catalog.
 *
 * This is the single place to edit when a tutorial is added or changed. Each
 * tutorial is a sequence of DOM-anchored steps rendered by `TourRunner` as a
 * spotlight + coachmark.
 *
 * Conventions:
 *  - `id` MUST be a stable, unique slug. It is what we persist as "completed",
 *    so never reuse an id and never rename one after release.
 *  - `roles` undefined = shown to everyone; otherwise only the listed roles.
 *  - `featureFlag` (optional) hides the tutorial until the flag is enabled.
 *  - A step's `target` is a `data-tour` slug — the engine resolves it to
 *    `[data-tour="slug"]`. Prefer `ensure` to open/expand/pick whatever UI
 *    the step needs before measuring. The engine never auto-advances: a
 *    missing target keeps retrying behind a coachmark the user controls, so
 *    gate steps whose UI can't exist for a role with per-step `roles`.
 */
export type TourRole = 'athlete' | 'coach' | 'admin';

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

/**
 * A DOM action the runner performs to *enable* UI required by a step
 * (open the AI drawer, expand a row, pick a calendar athlete, …).
 * `skipIfSelector` makes it idempotent: when that selector already matches,
 * the click is skipped so we never toggle an open panel closed.
 */
export interface TourPreAction {
  clickSelector: string;
  skipIfSelector?: string;
  /** Extra wait after the click for React to paint. Default 150ms. */
  settleMs?: number;
}

export interface TourStep {
  /** `data-tour` slug of the element to spotlight. */
  target: string;
  title: string;
  body: string;
  /**
   * Only show this step to these roles (the step's UI doesn't exist for
   * others). Undefined = every role the tutorial itself targets.
   */
  roles?: TourRole[];
  /** Coachmark placement relative to the target. Default: auto (bottom). */
  placement?: TourPlacement;
  /** Route the step lives on — the engine navigates there first. */
  route?: string;
  /** `?view=` query param value to set before measuring (coach dashboard tabs). */
  view?: string;
  /** `?tab=` query param value to set before measuring (athlete detail tabs). */
  tab?: string;
  /** Extra selector to await before measuring (async-loaded content). */
  waitForSelector?: string;
  /**
   * Ordered actions that enable whatever UI the step needs before we measure
   * the target (preferred). Prefer this over skipping when the UI can be opened.
   */
  ensure?: TourPreAction[];
  /**
   * Single pre-action (legacy). Prefer `ensure` for multiple prerequisites.
   * Still supported; merged ahead of `ensure` when both are set.
   */
  preAction?: TourPreAction;
  /** How long to await the target before skipping the step. Default 8000ms. */
  timeoutMs?: number;
}

/** Shared ensure: open the analytics ASK AI drawer if it isn't already open. */
const ENSURE_AI_DRAWER: TourPreAction = {
  clickSelector: '[data-tour="analytics-ai-handle"]',
  skipIfSelector: '[data-tour="analytics-ai-drawer"]',
  settleMs: 300,
};

/** Shared ensure: expand a team-health athlete row so body map / injury UI mounts. */
const ENSURE_TEAM_HEALTH_ROW: TourPreAction = {
  clickSelector: '[data-tour="team-health-row"]',
  skipIfSelector: '[data-tour="team-health-bodymap"]',
};

/** Shared ensure: select a calendar athlete so the grid / filters mount. */
const ENSURE_CALENDAR_ATHLETE: TourPreAction[] = [
  {
    // Open picker from empty-state CTA or toolbar Switch Athlete.
    clickSelector:
      '[data-tour="calendar-open-athlete-picker"], [data-tour="calendar-switch-athlete"]',
    skipIfSelector: '[data-tour="calendar-grid"], [data-tour="calendar-athlete-option"]',
  },
  {
    clickSelector: '[data-tour="calendar-athlete-option"]',
    skipIfSelector: '[data-tour="calendar-grid"]',
    settleMs: 400,
  },
];

export interface Tutorial {
  /** Stable unique slug — persisted as "completed". Never reuse or rename. */
  id: string;
  title: string;
  description: string;
  /** Target roles. Undefined = all roles. */
  roles?: TourRole[];
  /** Only show when this feature flag is enabled. */
  featureFlag?: string;
  /**
   * Route the tour starts on. Also drives the "On this screen" badge
   * (prefix match, so `/athlete` matches `/athlete/3/42`).
   */
  launchRoute: string;
  /**
   * Additional routes that count as the tour's screen (badge + auto-start).
   * E.g. `/dashboard` renders the coach dashboard for coaches, so coach
   * tutorials match both. Defaults to `[launchRoute]`.
   */
  matchRoutes?: string[];
  /**
   * False when `launchRoute` is parameterized and can't be navigated to
   * directly (e.g. `/athlete/:teamId/:athleteId`) — the menu then requires
   * the user to already be on a matching screen and shows `launchHint`.
   */
  navigable?: boolean;
  /** Shown in the menu when the tutorial can't be started from here. */
  launchHint?: string;
  /**
   * Every not-yet-completed tutorial auto-starts when the user reaches its
   * screen by default (multiple due tutorials on one screen chain, one at a
   * time, as each is finished; an abandoned tour re-offers on the next
   * visit). "Skip all tours" / Esc opts out of every remaining auto-start
   * tutorial. Set `false` to make a tutorial menu-only.
   */
  autoStart?: boolean;
  icon?: LucideIcon;
  steps: TourStep[];
}

export const TUTORIALS: Tutorial[] = [
  // -- T1 -- Coach Dashboard Orientation --------------------------------------
  {
    id: 'coach-dashboard-orientation',
    title: 'Coach Dashboard Orientation',
    description: 'A quick lap around the coach dashboard and its five tabs.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    // Coaches land on /dashboard, which renders the same coach dashboard.
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    autoStart: true,
    icon: LayoutDashboard,
    steps: [
      {
        target: 'coach-team-selector',
        route: '/coach-dashboard',
        title: 'Pick your team',
        body: 'Everything on this dashboard is scoped to one team. Switch teams here any time — the period selector next to it controls the date range for every chart.',
        placement: 'bottom',
      },
      {
        target: 'coach-tab-bar',
        title: 'Five tabs, one team',
        body: "The dashboard is organized into five tabs. We'll step through each one so you know where everything lives.",
        placement: 'bottom',
      },
      {
        target: 'coach-tab-overview',
        view: 'overview',
        title: 'Team Overview',
        body: 'Your daily starting point: team readiness, injury risk, insights, and data health at a glance.',
        placement: 'bottom',
      },
      {
        target: 'coach-tab-analytics',
        view: 'analytics',
        title: 'Analytics Dashboard',
        body: 'Deep-dive charts — recovery, sleep, strain, HR zones, mileage — filterable by athlete and data source.',
        placement: 'bottom',
      },
      {
        target: 'coach-tab-rankings',
        view: 'rankings',
        title: 'Rankings',
        body: 'Athletes ranked by readiness, HRV, sleep, and strain, so you can spot outliers fast.',
        placement: 'bottom',
      },
      {
        target: 'coach-tab-athletes',
        view: 'athletes',
        title: 'Athletes',
        body: 'The full roster. Double-click any athlete anywhere in the app to open their individual deep-dive.',
        placement: 'bottom',
      },
      {
        target: 'coach-tab-chat',
        view: 'chat',
        title: 'AI Chat',
        body: "Ask the AI assistant about your team's data in plain English. There's a dedicated tutorial for this — find it under Tutorials in the sidebar.",
        placement: 'bottom',
      },
    ],
  },

  // -- T2 -- Reading Team Readiness -------------------------------------------
  {
    id: 'reading-team-readiness',
    title: 'Reading Team Readiness',
    description: 'How to interpret the readiness card, radar, tiers, and data health.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    icon: HeartPulse,
    steps: [
      {
        target: 'overview-team-readiness',
        route: '/coach-dashboard',
        view: 'overview',
        title: 'Team Readiness Assessment',
        body: "This card aggregates every athlete's wearable data into one team-level readiness picture. It needs at least one athlete with synced data.",
        placement: 'bottom',
      },
      {
        target: 'overview-readiness-radar',
        view: 'overview',
        title: 'The readiness radar',
        body: "Four components feed the score: HRV, resting heart rate, sleep, and training load (ACWR on Garmin, Strain on WHOOP). A balanced shape is good; a collapsed axis shows you what's dragging the team down.",
        placement: 'right',
      },
      {
        target: 'overview-readiness-overall',
        view: 'overview',
        title: 'Overall score & tiers',
        body: 'The blended score maps to tiers: Optimal (80+, green), Moderate (60-79, yellow), Poor/Low (below 60, red). The footnote shows how many athletes actually have data behind it.',
        placement: 'left',
      },
      {
        target: 'overview-team-roster',
        view: 'overview',
        title: 'Per-athlete roster',
        body: "Each card shows readiness, load, and HRV per athlete. SYNCING means a wearable is connected but hasn't delivered data yet; NO DATA means nothing is connected. Click a card to open that athlete.",
        placement: 'top',
      },
      {
        target: 'overview-data-health',
        view: 'overview',
        title: 'Data Health',
        body: 'Coverage bars show how much of the roster is delivering Catapult, wearable, and check-in data. Low coverage here explains low-confidence scores everywhere else.',
        placement: 'top',
        timeoutMs: 10000,
      },
    ],
  },

  // -- T3 -- AI Chat & Drag-to-Analyze ----------------------------------------
  {
    id: 'ai-chat-drag-analyze',
    title: 'AI Chat & Chart Analysis',
    description: 'Ask the AI about your team and attach charts as context.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    icon: Bot,
    steps: [
      {
        target: 'analytics-ai-handle',
        route: '/coach-dashboard',
        view: 'analytics',
        title: 'ASK AI, right from the charts',
        body: "On the Analytics tab, this handle opens an AI chat drawer beside your charts (on mobile it's a floating button).",
        placement: 'left',
      },
      {
        target: 'analytics-ai-drawer',
        view: 'analytics',
        ensure: [ENSURE_AI_DRAWER],
        title: 'The chat drawer',
        body: 'Chat with the AI without leaving your charts. Conversations are saved per team.',
        placement: 'left',
      },
      {
        target: 'analytics-ai-history',
        view: 'analytics',
        ensure: [ENSURE_AI_DRAWER],
        title: 'Chat history',
        body: 'Reopen any previous conversation for this team from the history dropdown, or start a fresh one with "+ New chat".',
        placement: 'left',
      },
      {
        target: 'analytics-attachable-chart',
        view: 'analytics',
        // Charts are attachable only with the drawer open — open it first.
        ensure: [ENSURE_AI_DRAWER],
        title: 'Click a chart to attach it',
        body: 'While the drawer is open, charts become attachable — click one and its current data (respecting your athlete and period filters) is staged as context for your next question.',
        placement: 'top',
        timeoutMs: 10000,
      },
      {
        target: 'analytics-ai-attachments',
        view: 'analytics',
        ensure: [
          ENSURE_AI_DRAWER,
          // Stage a chart when possible so the composer shows attachment chips.
          {
            clickSelector: '[data-tour="analytics-attachable-chart"]',
            skipIfSelector: '[data-tour="analytics-ai-attachments"] [data-attachment-chip]',
            settleMs: 250,
          },
        ],
        title: 'Attachments & composer',
        body: "Attached charts appear here as chips — remove any you don't want, then ask your question. The AI answers using exactly the data you attached.",
        placement: 'left',
        timeoutMs: 8000,
      },
      {
        target: 'coach-chat-box',
        view: 'chat',
        title: 'The full AI Chat tab',
        body: "For longer conversations there's a dedicated chat tab, with a knowledge base panel where you can upload team documents for the AI to use.",
        placement: 'top',
      },
      {
        target: 'coach-chat-suggested',
        view: 'chat',
        title: 'Suggested questions',
        body: 'Not sure where to start? These prompts show the kinds of analysis the assistant is good at. (This card only appears on a fresh conversation.)',
        placement: 'top',
        timeoutMs: 5000,
      },
    ],
  },

  // -- T4 -- Injury Risk & Team Health -----------------------------------------
  {
    id: 'injury-risk-team-health',
    title: 'Injury Risk & Team Health',
    description: 'From the team risk panel to the body map and injury log.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    icon: Shield,
    steps: [
      {
        target: 'overview-injury-risk',
        route: '/coach-dashboard',
        view: 'overview',
        title: 'Team Injury Risk',
        body: 'Athletes ranked by modeled injury risk, with 14-day trend sparklines and the top contributing factor. Click a row for the narrative, or View to open the athlete.',
        placement: 'top',
        // Loading shell shares this anchor; engine waits for a stable painted box.
        timeoutMs: 12000,
      },
      {
        target: 'team-health-filter',
        route: '/team-health',
        title: 'Team Health & Injury Log',
        body: 'The Team Health page tracks subjective wellness (soreness, RPE, pain flags) and managed injury records. Search or filter to injured athletes only.',
        placement: 'bottom',
      },
      {
        target: 'team-health-row',
        route: '/team-health',
        title: 'One row per athlete',
        body: 'Latest check-in metrics, pain flags, and active injuries side by side. Click a row to expand the full detail.',
        placement: 'bottom',
        timeoutMs: 10000,
      },
      {
        target: 'team-health-bodymap',
        route: '/team-health',
        ensure: [ENSURE_TEAM_HEALTH_ROW],
        title: 'The body map',
        body: 'Reported pain regions plotted on a body silhouette. Click a region to filter the injury history to that area.',
        placement: 'right',
      },
      {
        target: 'team-health-injury-accordion',
        route: '/team-health',
        // Accordion lives inside the expanded row — expand first.
        ensure: [ENSURE_TEAM_HEALTH_ROW],
        title: 'Injury history',
        body: 'Injury records grouped by body region with status (active / improving / resolved). Add records and update status here as athletes recover.',
        placement: 'left',
        timeoutMs: 8000,
      },
    ],
  },

  // -- T5 -- Rankings -----------------------------------------------------------
  {
    id: 'rankings',
    title: 'Understanding Rankings',
    description: 'Averages vs latest values and how to read trend numbers.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    icon: Trophy,
    steps: [
      {
        target: 'rankings-intro',
        route: '/coach-dashboard',
        view: 'rankings',
        title: 'Trends, not just totals',
        body: 'Trend numbers compare an athlete to their own average: positive = above their normal (good), negative = below (needs attention).',
        placement: 'bottom',
      },
      {
        target: 'rankings-readiness-table',
        view: 'rankings',
        title: 'Readiness rankings',
        body: 'Sorted by composite readiness. The Days column tells you how much data backs each score — be skeptical of scores built on 1-2 days.',
        placement: 'right',
      },
      {
        target: 'rankings-hrv-table',
        view: 'rankings',
        title: 'Average vs Latest',
        body: 'HRV shows both the period average and the most recent reading. A latest value well below average is an early recovery red flag, even if the average still looks fine.',
        placement: 'left',
      },
    ],
  },

  // -- T6 -- Inviting Athletes & Join Codes -------------------------------------
  {
    id: 'inviting-athletes',
    title: 'Inviting Athletes & Join Codes',
    description: 'Get your roster onboarded with invites or self-serve codes.',
    roles: ['coach', 'admin'],
    launchRoute: '/coach-dashboard',
    matchRoutes: ['/coach-dashboard', '/dashboard'],
    // Menu-only: onboarding is a one-time job, not something to auto-nag
    // every coach about on the dashboard.
    autoStart: false,
    icon: UserPlus,
    steps: [
      {
        target: 'sidebar-invite-user',
        route: '/coach-dashboard',
        title: 'Invite your athletes',
        body: "Fastest path: Invite a New User, right here in the sidebar — enter emails and we'll send each person a signup link that puts them straight onto your team.",
        placement: 'right',
      },
      {
        target: 'codes-athlete-code',
        route: '/team-information',
        tab: 'codes',
        title: 'Athlete join code',
        body: 'Prefer self-serve? Share this code (Team Information → Join Codes) and athletes can register themselves straight onto your team.',
        placement: 'left',
        timeoutMs: 10000,
      },
      {
        target: 'codes-coach-code',
        route: '/team-information',
        tab: 'codes',
        title: 'Coach join code',
        body: 'This separate code is for assistant coaches and staff — it grants coach access, so only share it with staff.',
        placement: 'right',
      },
      {
        target: 'wearable-team-devices',
        route: '/connect-wearable',
        title: 'Team hardware',
        body: 'Force plates and GPS trackers (Hawkin, Catapult) connect at the team level here — athletes connect their personal wearables themselves.',
        placement: 'top',
      },
    ],
  },

  // -- T7 -- Personal Dashboard Orientation --------------------------------------
  {
    id: 'personal-dashboard-orientation',
    title: 'Personal Dashboard Orientation',
    description: 'Where your recovery, sleep, and activity data lives.',
    roles: ['athlete'],
    launchRoute: '/dashboard',
    autoStart: true,
    icon: LayoutDashboard,
    steps: [
      {
        target: 'personal-category-filter',
        route: '/dashboard',
        title: 'Filter your charts',
        body: 'Your dashboard can hold a lot of charts. Use these categories to focus on recovery, sleep, activity, or cardio data.',
        placement: 'bottom',
      },
      {
        target: 'personal-recovery-stats',
        title: 'Your HRV at a glance',
        body: 'Latest, average, highest, and lowest heart-rate variability for the selected period. HRV is the single best everyday indicator of how recovered you are.',
        placement: 'bottom',
        timeoutMs: 10000,
      },
      {
        target: 'personal-hrv-chart',
        title: 'HRV trend',
        body: 'Watch the direction, not single days: a sustained downward HRV trend usually means accumulating fatigue before you feel it.',
        placement: 'top',
      },
      {
        target: 'personal-sleep-chart',
        title: 'Sleep hours',
        body: 'Nightly sleep duration from your wearable. Consistency matters as much as total hours.',
        placement: 'top',
      },
      {
        target: 'personal-activity-section',
        title: 'Activity strain',
        body: 'Daily cardiovascular strain per session. Big strain days should be followed by strong recovery — if HRV drops after spikes here, back off.',
        placement: 'top',
      },
    ],
  },

  // -- T8 -- Daily Check-in -------------------------------------------------------
  {
    id: 'daily-checkin',
    title: 'Daily Check-in',
    description: 'Log soreness, RPE, and pain in 30 seconds.',
    roles: ['athlete'],
    launchRoute: '/daily-checkin/today',
    icon: ClipboardList,
    steps: [
      {
        target: 'checkin-form',
        route: '/daily-checkin/today',
        title: 'Your daily check-in',
        body: 'This 30-second form is how your coach sees what the wearables can\'t: how training actually felt. If you rested, just tick "I didn\'t train today".',
        placement: 'top',
      },
      {
        target: 'checkin-soreness',
        title: 'Soreness',
        body: 'Rate overall muscle soreness from 1 (none) to 10 (extreme). Persistent high soreness is one of the strongest injury-risk signals we track.',
        placement: 'bottom',
      },
      {
        target: 'checkin-rpe',
        title: 'Session difficulty (RPE)',
        body: 'How hard the session felt, 1-10. When your RPE runs much higher than the planned load, that mismatch flags early overtraining.',
        placement: 'top',
      },
      {
        target: 'checkin-body-diagram',
        title: 'Pinpoint pain',
        body: "Feeling pain somewhere specific? Mark the body region and intensity — it shows up on your coach's body map and feeds the injury-risk model.",
        placement: 'top',
      },
      {
        target: 'checkin-submit',
        title: 'Submit',
        body: "That's it. Your responses appear alongside your recovery metrics on your coach's dashboard immediately.",
        placement: 'top',
      },
    ],
  },

  // -- T9 -- Connecting a Wearable --------------------------------------------------
  {
    id: 'connecting-wearable',
    title: 'Connecting a Wearable',
    description: 'Link WHOOP, Garmin, Oura, and more in a couple of clicks.',
    launchRoute: '/connect-wearable',
    icon: Watch,
    steps: [
      {
        target: 'wearable-personal-devices',
        route: '/connect-wearable',
        title: 'Personal devices',
        body: 'Pick the device you wear. Each connects through its own secure sign-in (OAuth) — we never see your device account password.',
        placement: 'bottom',
      },
      {
        target: 'wearable-whoop-card',
        title: 'One click to connect',
        body: 'Click a device card to start its sign-in flow. Once authorized, data syncs automatically — a green check marks devices that are already connected.',
        placement: 'right',
      },
      {
        target: 'wearable-team-devices',
        // The team-devices section only renders for staff.
        roles: ['coach', 'admin'],
        title: 'Team devices',
        body: 'Coaches connect team-wide hardware — Catapult GPS and Hawkin force plates — here. These sync data for the whole roster at once.',
        placement: 'top',
      },
    ],
  },

  // -- T10 -- Athlete Deep-Dive -------------------------------------------------------
  {
    id: 'athlete-deep-dive',
    title: 'Athlete Deep-Dive',
    description: "Reading an individual athlete's readiness, risk, and trends.",
    roles: ['coach', 'admin'],
    launchRoute: '/athlete',
    navigable: false,
    launchHint: 'Open an athlete first (double-click one on the coach dashboard).',
    icon: Users,
    steps: [
      {
        target: 'athlete-tab-bar',
        title: 'The athlete profile',
        body: 'Everything about one athlete, organized into five tabs. You can reach this page by double-clicking an athlete anywhere on the coach dashboard.',
        placement: 'bottom',
      },
      {
        target: 'athlete-readiness-score',
        tab: 'overview',
        title: 'Athlete status',
        body: 'Readiness and injury risk in one card. Expand it for the component-by-component breakdown behind each score.',
        placement: 'bottom',
        timeoutMs: 12000,
      },
      {
        target: 'athlete-signal-contributors',
        tab: 'overview',
        ensure: [
          {
            // Expand the status card so the Injury Risk tab is interactable.
            clickSelector: '[data-tour="athlete-status-expand-toggle"]',
            skipIfSelector: '[data-tour="athlete-status-expand-toggle"][aria-expanded="true"]',
          },
          {
            clickSelector: '[data-tour="athlete-status-injury-tab"]',
            skipIfSelector: '[data-tour="athlete-signal-contributors"]',
            settleMs: 250,
          },
        ],
        title: "What's driving the risk",
        body: 'The top contributing signals — soreness, HRV drops, load spikes — each with its recent trend. This is the "why" behind the risk number.',
        placement: 'top',
        timeoutMs: 8000,
      },
      {
        target: 'athlete-tab-recovery',
        tab: 'recovery',
        title: 'Recovery',
        body: 'Daily recovery / HRV history for this athlete over the selected period.',
        placement: 'bottom',
      },
      {
        target: 'athlete-tab-sleep',
        tab: 'sleep',
        title: 'Sleep',
        body: 'Sleep duration, quality, and stages night by night.',
        placement: 'bottom',
      },
      {
        target: 'athlete-tab-activity',
        tab: 'activity',
        title: 'Activity',
        body: 'Training sessions and strain over time.',
        placement: 'bottom',
      },
      {
        target: 'athlete-tab-ai-insights',
        tab: 'ai-insights',
        title: 'AI Insights',
        body: "A generated narrative summary of this athlete's data — useful before a 1:1 conversation.",
        placement: 'bottom',
      },
    ],
  },

  // -- T11 -- Calendar Tour --------------------------------------------------------------
  {
    id: 'calendar-tour',
    title: 'Training Calendar',
    description: 'All wearable and performance data on one calendar.',
    featureFlag: 'calendar_v1',
    launchRoute: '/calendar',
    icon: Calendar,
    steps: [
      {
        target: 'calendar-period-nav',
        route: '/calendar',
        // Coaches land here with the athlete picker auto-opened (no athlete
        // selected yet) — pick one so the modal isn't sitting inside the
        // toolbar cutout. Idempotent: skipped once the grid exists.
        ensure: ENSURE_CALENDAR_ATHLETE,
        title: 'The unified calendar',
        body: 'Every data source — Garmin, WHOOP, Catapult, Hawkin and more — on one calendar. Use the arrows to move through time, click the period label to jump to today, and switch between Month and Week.',
        placement: 'bottom',
      },
      {
        target: 'calendar-switch-athlete',
        // Athletes never see this control — the step only exists for staff.
        roles: ['coach', 'admin'],
        ensure: ENSURE_CALENDAR_ATHLETE,
        title: 'Switch athlete',
        body: 'Coaches view one athlete at a time. Use this button (or Cmd/Ctrl-K) to jump to anyone on your roster.',
        placement: 'bottom',
      },
      {
        target: 'calendar-source-filters',
        title: 'Source filters',
        body: "Toggle data sources on and off — each chip also shows how fresh that source's data is. Keys 1-7 toggle them from the keyboard.",
        placement: 'bottom',
        ensure: ENSURE_CALENDAR_ATHLETE,
        timeoutMs: 10000,
      },
      {
        target: 'calendar-grid',
        title: 'The grid',
        body: 'Each day shows logos for the sources that recorded data. Navigate with arrow keys, press T for today, and click any day to open it.',
        placement: 'top',
        ensure: ENSURE_CALENDAR_ATHLETE,
        timeoutMs: 10000,
      },
      {
        target: 'calendar-day-panel',
        ensure: [
          ...ENSURE_CALENDAR_ATHLETE,
          {
            // Prefer today (always inside the loaded range)…
            clickSelector: '[data-tour="calendar-grid"] button[data-today="true"]',
            skipIfSelector: '[data-tour="calendar-day-panel"]',
            settleMs: 300,
          },
          {
            // …falling back to any in-month day cell.
            clickSelector: '[data-tour="calendar-grid"] button[data-current-month="true"], [data-tour="calendar-grid"] button',
            skipIfSelector: '[data-tour="calendar-day-panel"]',
            settleMs: 300,
          },
        ],
        title: 'Day detail',
        body: 'Click any day to open its panel — it breaks down sleep, workouts, sessions, and assessments for that date. Press Escape to close it.',
        placement: 'left',
        timeoutMs: 10000,
      },
    ],
  },

  // -- T12 -- Settings & Email Digests -----------------------------------------------------
  {
    id: 'settings-email-digests',
    title: 'Settings & Email Digests',
    description: 'Digest emails, readiness alerts, and theme.',
    roles: ['coach', 'admin'],
    launchRoute: '/settings',
    icon: Settings,
    steps: [
      {
        target: 'settings-digest',
        route: '/settings',
        title: 'Digest notifications',
        body: "Get a periodic email summary of your team's readiness and flags, so you stay informed without opening the dashboard every day.",
        placement: 'top',
        timeoutMs: 8000,
      },
      {
        target: 'settings-readiness-alerts',
        route: '/settings',
        title: 'Readiness alerts',
        body: "Set a readiness threshold per team and we'll email you when any athlete drops below it — your early-warning system.",
        placement: 'top',
        timeoutMs: 8000,
      },
      {
        target: 'sidebar-theme-toggle',
        title: 'Light or dark',
        body: 'Toggle the whole app between light and dark mode from the sidebar, any time.',
        placement: 'right',
      },
    ],
  },

  // -- T13 -- AI Agent Inbox ------------------------------------------------------------------
  {
    id: 'agent-inbox',
    title: 'AI Agent Inbox',
    description: 'Review and approve AI-proposed athlete outreach.',
    roles: ['coach', 'admin'],
    featureFlag: 'agent_sms_v2',
    launchRoute: '/agent',
    icon: Sparkles,
    steps: [
      {
        target: 'agent-tabs',
        route: '/agent',
        title: 'Proposal buckets',
        body: "The AI agent watches your team's data and proposes actions (like an SMS check-in when risk escalates). Proposals move through pending, then approved / rejected / expired.",
        placement: 'bottom',
      },
      {
        target: 'agent-proposal-list',
        route: '/agent',
        title: 'Proposals',
        body: 'Each card shows the athlete, what triggered the proposal, and a summary. Click one to review the proposed actions in full.',
        placement: 'top',
      },
      {
        target: 'agent-approval-bar',
        preAction: {
          clickSelector: '[data-tour="agent-proposal-list"] a',
          skipIfSelector: '[data-tour="agent-approval-bar"]',
        },
        title: 'You stay in control',
        body: 'Nothing is sent without you. Select the actions you agree with (editing them if needed) and approve — or reject the whole proposal.',
        placement: 'top',
        timeoutMs: 8000,
      },
    ],
  },
];
